import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db } from '../data/store';
import { collection, doc, setDoc, getDocs, onSnapshot, query, where, newId, writeBatch } from '@battleops/localstore';
import {
  buildDepartmentTree, listDepartments, departmentOf, GENERAL_DEPARTMENT_ID, DEPARTMENT_DEFAULTS,
  typeOf, makeProjectType, cloneProjectType, isProjectLive,
  isDepartmentMode, seedTaskDepartments, merge, set,
  departmentChoices, effectiveDepartment, stepDepartment as stepOption, syncedDepartmentToAdopt, ALL_DEPARTMENTS, resolveType, typesForDepartment } from '@battleops/shared';
import { commitWrites } from '../data/commit';
import { useSession } from './SessionContext';
import { platform } from '../platform';
import { getDeviceId } from '../session/session';

// The switcher's pick is per device (kv), not synced — Lj reviews in one department while working in another.
// Hold-to-sync sends it to his other devices through meta/settings.departmentSync (taskdepartments.js).
const KV_CURRENT = 'ops.department.current';
const KV_SYNC_AT = 'ops.department.syncAt';
const kvGet = (k) => { try { return Promise.resolve(platform().kv.getItem(k)).catch(() => null); } catch { return Promise.resolve(null); } };
const kvSet = (k, v) => { try { Promise.resolve(platform().kv.setItem(k, String(v))).catch(() => {}); } catch { /* no kv */ } };
import { useProjects } from './ProjectContext';

// Departments (Dev, Field, Office, Personal…) — the top level every project lives in, and the
// rules its projects inherit (see packages/shared/src/hierarchy.js). Replaces Signo's
// domain/subdomain hierarchy. Folders stay as optional grouping inside a department.
const DepartmentContext = createContext();

const DEPARTMENTS = 'departments';
const TYPES = 'projectTypes';
const FOLDERS = 'folders';
const PROJECTS = 'projects';
const TASKS = 'tasks';
const SETTINGS = ['name', 'color', 'icon', 'order', 'clock', 'track', 'feedBooks', 'billable', 'hourlyRate', 'homeProjectId', 'quickTasks', 'scoreMetrics', 'goals', 'homeProjectIds', 'boards', 'boardsOn', 'defaultBoardId', 'taskDefaults'];   // boards (docs/plans/boards.md): its boards, on/off, which one opens   // homeProjectIds = the projects pinned to its Home, in order   // + the desk: home project, quick tasks, its 3 score metrics

export function DepartmentProvider({ children }) {
  const { projects } = useProjects();
  const access = useSession()?.access ?? null;   // tests render this without a session
  const [currentPick, setCurrentPick] = useState(ALL_DEPARTMENTS);
  const picked = useRef(false);   // set once anything chooses — the stored pick then doesn't overwrite it
  const [syncAt, setSyncAt] = useState(null);   // null until the device's last taken-up sync is read
  const [departments, setDepartments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [types, setTypes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const a = onSnapshot(collection(db, DEPARTMENTS),
      (snap) => { setDepartments(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoaded(true); },
      () => { setDepartments([]); setLoaded(true); });
    const b = onSnapshot(collection(db, FOLDERS),
      (snap) => setFolders(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setFolders([]));
    const c = onSnapshot(collection(db, TYPES),
      (snap) => setTypes(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((x, y) => String(x.name).localeCompare(String(y.name)))),
      () => setTypes([]));
    const d = onSnapshot(doc(db, 'meta', 'settings'), (snap) => setSettings(snap.exists() ? snap.data() : {}), () => setSettings({}));
    return () => { a(); b(); c(); d(); };
  }, []);

  const list = useMemo(() => listDepartments(departments), [departments]);
  // Live (active or on hold) jobs make the tree; completed / cancelled / archived leave it (shared/projectstatus).
  const activeProjects = useMemo(() => projects.filter(isProjectLive), [projects]);
  const tree = useMemo(() => buildDepartmentTree(departments, activeProjects, folders), [departments, activeProjects, folders]);

  const deptForProject = useCallback((projectId) => {
    const p = projectId ? projects.find((x) => x.id === projectId) : null;
    return departmentOf(p, departments);
  }, [projects, departments]);
  const projectIdsFor = useCallback((departmentId) => new Set(projects.filter((p) => departmentOf(p, departments).id === departmentId).map((p) => p.id)), [projects, departments]);

  // ── Project types ────────────────────────────────────────────────────────────────────────
  const typeForProject = useCallback((projectId) => {
    const p = projectId ? projects.find((x) => x.id === projectId) : null;
    return resolveType(typeOf(p, types), types);
  }, [projects, types]);
  const typesFor = useCallback((departmentId) => typesForDepartment(types, departmentId, GENERAL_DEPARTMENT_ID), [types]);

  const saveType = useCallback(async (data) => {
    const id = data.id || newId();
    const existing = types.find((t) => t.id === id);
    const next = { ...makeProjectType(), ...(existing || {}), ...data, id, updatedAt: Date.now(), createdAt: existing?.createdAt || Date.now() };
    await setDoc(doc(db, TYPES, id), next);
    return next;
  }, [types]);

  const cloneType = useCallback(async (type, name) => saveType({ ...cloneProjectType(type, name) }), [saveType]);

  // Projects of a deleted type keep their phases and tasks; they just stop running its checklists.
  const deleteType = useCallback(async (id) => {
    const snap = await getDocs(query(collection(db, PROJECTS), where('typeId', '==', id)));
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.update(doc(db, PROJECTS, d.id), { typeId: null }));
    batch.delete(doc(db, TYPES, id));
    await batch.commit();
  }, []);

  // Create (no id) or update (id). General is a fixed id, created on its first edit.
  const saveDepartment = useCallback(async (data) => {
    const id = data.id || newId();
    const existing = departments.find((d) => d.id === id);
    const base = existing || (id === GENERAL_DEPARTMENT_ID ? { ...DEPARTMENT_DEFAULTS } : { ...DEPARTMENT_DEFAULTS, name: '', order: Date.now() });
    const next = { ...base, id, updatedAt: Date.now(), createdAt: base.createdAt || Date.now() };
    for (const k of SETTINGS) if (data[k] !== undefined) next[k] = data[k];
    await setDoc(doc(db, DEPARTMENTS, id), next);
    return next;
  }, [departments]);

  // Department mode (experimental, docs/plans/departments.md): the task carries the department. The first
  // turn-on copies each project's department onto its top-level tasks (shared/taskdepartments.js), in the
  // same group as the switch; after that, on/off is just the switch and nothing is lost either way.
  const departmentMode = isDepartmentMode(settings);

  // ── The switcher (D2) ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Sticky per device (Lj 2026-10-26: "wherever we leave off is where we should come back … my phone's probably on
    // Field still"): the last pick comes back on this device unless something already chose; nothing here follows
    // the clock. (Until 10-26 the app opened on All every time — Lj 10-10 — that was reversed.)
    kvGet(KV_SYNC_AT).then((at) => setSyncAt(Number(at) || 0));
    kvGet(KV_CURRENT).then((id) => { if (id && !picked.current) setCurrentPick(String(id)); });
  }, []);
  const choices = useMemo(() => departmentChoices(departments, access), [departments, access]);
  // A member with department grants always works in their departments; for everyone else it's the experiment's switch.
  const departmentActive = departmentMode || (!!access && Object.keys(access.departments || {}).length > 0);
  const current = departmentActive ? effectiveDepartment(choices.options, currentPick) : null;
  const currentDepartmentId = current ? current.id : ALL_DEPARTMENTS;
  const setCurrentDepartment = useCallback((id) => { picked.current = true; setCurrentPick(id); kvSet(KV_CURRENT, id); }, []);
  const stepDepartment = useCallback((dir) => {
    const next = stepOption(choices.options, currentDepartmentId, dir);
    if (next) setCurrentDepartment(next);
  }, [choices, currentDepartmentId, setCurrentDepartment]);
  // Hold: send this device's department to the others (each takes it up once).
  const syncDepartment = useCallback(async () => {
    const at = Date.now();
    setSyncAt(at); kvSet(KV_SYNC_AT, at);
    const deviceId = await getDeviceId().catch(() => null);
    await commitWrites([merge('meta/settings', { departmentSync: { id: currentDepartmentId, at, deviceId }, updatedAt: at })]);
  }, [currentDepartmentId]);
  useEffect(() => {
    if (syncAt === null) return;
    const id = syncedDepartmentToAdopt(settings?.departmentSync, syncAt);
    if (!id) return;
    const at = settings.departmentSync.at;
    setSyncAt(at); kvSet(KV_SYNC_AT, at);
    setCurrentDepartment(id);
  }, [settings?.departmentSync, syncAt, setCurrentDepartment]);
  const setDepartmentMode = useCallback(async (on) => {
    const at = Date.now();
    const writes = [];
    if (on && !settings?.departmentModeSeededAt) {
      const [t, p] = await Promise.all([getDocs(collection(db, TASKS)), getDocs(collection(db, PROJECTS))]);
      const rows = (snap) => snap.docs.map((x) => ({ id: x.id, ...x.data() }));
      writes.push(...seedTaskDepartments({ projects: rows(p), tasks: rows(t), departments, now: at }));
      // Lj 2026-10-01: start with Field and Personal (everything else General until it's figured out).
      if (!departments.some((d) => /^field$/i.test(String(d.name || '')))) {
        const id = newId();
        writes.push(set(`departments/${id}`, { ...DEPARTMENT_DEFAULTS, id, name: 'Field', icon: '🔨', color: '#E8833A', order: 0, createdAt: at, updatedAt: at }));
      }
    }
    writes.push(merge('meta/settings', { departmentMode: !!on, ...(on && !settings?.departmentModeSeededAt ? { departmentModeSeededAt: at } : {}), updatedAt: at }));
    await commitWrites(writes);
    return writes.filter((w) => w.path.startsWith('tasks/')).length;   // tasks given a department
  }, [settings, departments]);

  // Projects and tasks fall back to General; one batch so the move lands whole.
  const deleteDepartment = useCallback(async (id) => {
    if (!id || id === GENERAL_DEPARTMENT_ID) return;
    const snap = await getDocs(query(collection(db, PROJECTS), where('departmentId', '==', id)));
    const tsnap = await getDocs(query(collection(db, TASKS), where('departmentId', '==', id)));
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.update(doc(db, PROJECTS, d.id), { departmentId: null }));
    tsnap.docs.forEach((d) => batch.update(doc(db, TASKS, d.id), { departmentId: null, updatedAt: Date.now() }));
    batch.delete(doc(db, DEPARTMENTS, id));
    await batch.commit();
  }, []);

  // Up/down in the list: renumber everyone (gapped) in one batch.
  const moveDepartment = useCallback(async (id, dir) => {
    const ids = list.map((d) => d.id);
    const i = ids.indexOf(id);
    const j = dir === 'up' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    const batch = writeBatch(db);
    ids.forEach((did, k) => {
      const cur = list.find((d) => d.id === did);
      const saved = departments.find((d) => d.id === did);
      batch.set(doc(db, DEPARTMENTS, did), { ...(saved || cur), id: did, order: (k + 1) * 1000, updatedAt: Date.now() });
    });
    await batch.commit();
  }, [list, departments]);

  const deleteFolder = useCallback(async (id) => {
    const batch = writeBatch(db);
    const projSnap = await getDocs(query(collection(db, PROJECTS), where('folderId', '==', id)));
    projSnap.docs.forEach((d) => batch.update(doc(db, PROJECTS, d.id), { folderId: null }));
    const f = folders.find((x) => x.id === id);
    const childSnap = await getDocs(query(collection(db, FOLDERS), where('parentId', '==', id)));
    childSnap.docs.forEach((d) => batch.update(doc(db, FOLDERS, d.id), { parentType: f?.parentType || 'department', parentId: f?.parentId || null }));
    batch.delete(doc(db, FOLDERS, id));
    await batch.commit();
  }, [folders]);

  return (
    <DepartmentContext.Provider value={{
      departments: list, rawDepartments: departments, folders, tree, loaded,
      departmentMode, setDepartmentMode,
      departmentActive, departmentOptions: choices.options, departmentLocked: choices.locked,
      currentDepartment: current, currentDepartmentId, setCurrentDepartment, stepDepartment, syncDepartment,
      deptForProject, projectIdsFor, saveDepartment, deleteDepartment, moveDepartment, deleteFolder,
      types, typeForProject, typesFor, saveType, cloneType, deleteType,
    }}>
      {children}
    </DepartmentContext.Provider>
  );
}

export function useDepartments() {
  const ctx = useContext(DepartmentContext);
  if (!ctx) throw new Error('useDepartments must be used within DepartmentProvider');
  return ctx;
}
